import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { LESSONS } from '../store/actionTypes';
import { Pagination } from './models/response';
import { Lesson } from './models/lessons';

export const lessons = createApi({
  reducerPath: LESSONS,
  baseQuery,
  tagTypes: ['Lessons'],
  endpoints: builder => ({
    getMyLessons: builder.query<
      { data: Lesson[]; pagination: Pagination },
      {
        page: number;
        limit: number;
        offset?: number;
        earliest?: string;
        latest?: string;
        status?: string;
      }
    >({
      query: variables => ({
        document: gql`
          query GetMyLessons(
            $page: Int
            $limit: Int
            $latest: String
            $earliest: String
            $offset: Int
            $status: String
          ) {
            getMyLessons(
              page: $page
              limit: $limit
              latest: $latest
              earliest: $earliest
              offset: $offset
              status: $status
            ) {
              pagination {
                items
                pages
              }
              data {
                name {
                  language
                  displayName
                }
                meta {
                  updated
                  created
                }
              }
            }
          }
        `,
        variables,
      }),
      providesTags: ['Lessons'],
      transformResponse: (response: any) => response?.getMyLessons,
    }),

    getLesson: builder.query<Lesson, { lessonId: string }>({
      query: variables => ({
        document: gql`
          query GetLesson($lessonId: String) {
            getLesson(lessonId: $lessonId) {
              id
              description {
                language
                text
              }
              organizer {
                email
                user {
                  profile {
                    firstname
                    lastname
                    avatar {
                      contentUrl
                    }
                    bio
                  }
                }
              }
              event {
                startTime {
                  dateTime
                }
              }
              name {
                displayName
                language
              }
            }
          }
        `,
        variables,
      }),
      providesTags: ['Lessons'],
      transformResponse: (response: any) => response?.getLesson,
    }),

    updatePreference: builder.mutation<Lesson, { lessonId: string; description: string }>({
      query: variables => ({
        document: gql`
          mutation UpdatePreference($description: String!, $lessonId: String!) {
            updatePreference(description: $description, lessonId: $lessonId) {
              id
              description {
                text
                language
              }
            }
          }
        `,
        variables,
      }),
      invalidatesTags: ['Lessons'],
      transformResponse: (response: any) => response?.updatePreference,
    }),
  }),
});

export const {
  useGetMyLessonsQuery,
  useLazyGetMyLessonsQuery,
  useGetLessonQuery,
  useUpdatePreferenceMutation,
} = lessons;
